import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";

import {
  useSelector,
  useDispatch,
} from "react-redux";

import LayoutHeaderSidebar from "../users/LayoutHeaderSidebar";
import { postData, getData } from "../../services/FetchNodeservices";

/* ================================================= */
/* PAGES */
/* ================================================= */

import Home from "./Home";
import TalentPool from "./TalentPool";
import TalentPoolResults from "./TalentPoolResults";
import TimeTracking from "./TimeTracking";
import CallingSheet from "./CallingSheet";
import BusinessReport from "./BusinessReport";
import JobOpening from "./JobOpening";
import JobTable from "./JobTable";
import Customizetable from "./Customizetable";
import MyReports from "./MyReport";
import UserSubmissionReport from "./UserSubmissionReport";
import HierarchyOrgChart from "./HierarchyOrgChart";
import JobAssignments from "./JobAssignments";
import ManagerDashboard from "./ManagerDashboard";
import DirectorDashboard from "./DirectorDashboard";
import MyPayslip from "./MyPayslip";
import Vendor from "../admin/pages/Vendor";

/* ================================================= */
/* ICONS */
/* ================================================= */

import {

  HomeIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleOvalLeftIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon,
  UsersIcon,
  UserCircleIcon,
  ChartPieIcon,
  ClipboardDocumentCheckIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";

export default function Dashboard() {

  // =================================================
  // NAVIGATION
  // =================================================

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const dispatch =
    useDispatch();

  // =================================================
  // USER
  // =================================================

  const user =
    useSelector(
      (state) => state.user
    );

  // =================================================
  // DROPDOWN
  // =================================================

  const [openProfile,
    setOpenProfile] =
    useState(false);

  const [theme, setTheme] = useState(localStorage.getItem("THEME") || "light");

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("THEME", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const [notifications, setNotifications] = useState([]);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchNotifications = async () => {
    if (!user?.id) return;
    try {
      const response = await getData(`notifications/fetch/${user.id}`);
      if (response?.status) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      const timer = setInterval(fetchNotifications, 10000); // Live update every 10 seconds
      return () => clearInterval(timer);
    }
  }, [user]);

  const handleMarkRead = async (id, link) => {
    try {
      await postData(`notifications/read/${id}`);
      fetchNotifications();
      setOpenNotifications(false);
      if (link) {
        navigate(link);
      }
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await postData(`notifications/read-all/${user.id}`);
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // =================================================
  // AUTH CHECK
  // =================================================

  useEffect(() => {

    if (!user) {

      navigate(
        "/",
        {
          replace: true,
        }
      );
    }

  }, [
    user,
    navigate,
  ]);

  // =================================================
  // AUTO LOGOUT CHECK (9 HOURS SESSION TIMEOUT)
  // =================================================

  useEffect(() => {
    const checkSessionTimeout = async () => {
      const loginTimeStr = localStorage.getItem("LOGIN_TIME");
      if (loginTimeStr) {
        const loginTime = parseInt(loginTimeStr, 10);
        if (!isNaN(loginTime)) {
          const elapsed = Date.now() - loginTime;
          const nineHoursMs = 9 * 60 * 60 * 1000;
          if (elapsed >= nineHoursMs) {
            console.log("Session expired (9 hours limit reached). Auto logging out...");
            if (user?.id) {
              try {
                await postData("attendances/logout", { user_id: user.id });
              } catch (e) {
                console.error("Auto logout attendance check failed:", e);
              }
            }
            localStorage.clear();
            dispatch({
              type: "REMOVE_USER"
            });
            navigate("/", { replace: true });
          }
        }
      } else if (user?.id) {
        // Fallback: If logged in but no LOGIN_TIME is stored, initialize it to now to start countdown
        localStorage.setItem("LOGIN_TIME", Date.now().toString());
      }
    };

    checkSessionTimeout();
    const intervalId = setInterval(checkSessionTimeout, 15000); // Check every 15 seconds
    return () => clearInterval(intervalId);
  }, [user, navigate, dispatch]);

  // =================================================
  // ROLE ACCESS
  // =================================================



  // =================================================
  // LOGOUT
  // =================================================

  const logout =
    () => {

      localStorage.clear();

      dispatch({
        type: "ADD_USER",
        payload: null,
      });

      navigate("/");
    };

  // =================================================
  // MENU ITEMS
  // =================================================

  const menuItems =
    useMemo(() => {

      const items = [

        {
          text:
            "Dashboard",

          icon:
            <Squares2X2Icon className="w-5 h-5" />,

          path:
            "/dashboard",
        },

        {
          text:
            "Talent Pool",

          icon:
            <UsersIcon className="w-5 h-5" />,

          path:
            "/dashboard/talentpool",
        },

        {
          text:
            "Time Tracking",

          icon:
            <ClockIcon className="w-5 h-5" />,

          path:
            "/dashboard/timetracking",
        },

        {
          text:
            "Manage Team",

          icon:
            <UserGroupIcon className="w-5 h-5" />,

          path:
            "/dashboard/hierarchycrgchart",
        },

        {
          text:
            "Calling Sheet",

          icon:
            <ChatBubbleOvalLeftIcon className="w-5 h-5" />,

          path:
            "/dashboard/callingsheet",
        },

        {
          text:
            "Job Openings",

          icon:
            <BriefcaseIcon className="w-5 h-5" />,

          path:
            "/dashboard/jobopening",
        },

        {
          text:
            "Vendors",

          icon:
            <BuildingOfficeIcon className="w-5 h-5" />,

          path:
            "/dashboard/vendor",
        },

        {
          text:
            "Working Task",

          icon:
            <ClipboardDocumentListIcon className="w-5 h-5" />,

          path:
            "/dashboard/workingeod",
        },

        {
          text:
            "My Reports",

          icon:
            <DocumentChartBarIcon className="w-5 h-5" />,

          path:
            "/dashboard/myreport",
        },


{
  text:
    "Payslips",

  icon:
    <DocumentChartBarIcon className="w-5 h-5" />,

  path:
    "/dashboard/payslip",
},


        {
          text:
            "Job Assignments",

          icon:
            <ClipboardDocumentCheckIcon className="w-5 h-5" />,

          path:
            "/dashboard/jobassignment",
        },
      ];

      if (
        user &&
        user.designation_name !== "Manager" &&
        (user.can_view_reports === 1 ||
          user.designation_name === "Director" ||
          user.designation_name === "Vice President" ||
          user.designation_name === "Account Manager")
      ) {

        items.splice(
          5,
          0,
          {

            text:
              "Business Reports",

            icon:
              <ChartPieIcon className="w-5 h-5" />,

            path:
              "/dashboard/businessreport",
          }
        );
      }

      return items;

    }, [user]);

  // =================================================
  // RETURN
  // =================================================

  return (
    <div className="flex min-h-screen bg-[#f4f6fa] text-gray-800 font-sans">
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden"
        />
      )}

      {/* ================================================= */}
      {/* SIDEBAR */}
      {/* ================================================= */}
      <aside
        className={`
          fixed
          left-0
          top-0
          z-50
          h-screen
          w-[260px]
          bg-white
          border-r
          border-gray-100
          flex
          flex-col
          transition-transform
          duration-300
          ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        {/* LOGO */}
        <div className="h-[72px] flex items-center justify-between px-6 border-b border-gray-50">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Morgan Logo" className="h-10 object-contain" />
          </div>

          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 text-gray-400 rounded-lg hover:bg-gray-50 md:hidden"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* MENU */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-1.5 no-scrollbar">
          {menuItems.map((item, index) => {
            const active = location.pathname === item.path;
            return (
              <div key={index} className="relative w-full">
                {active && (
                  <div className="absolute left-[-16px] top-1/2 -translate-y-1/2 w-[5px] h-7 bg-indigo-600 rounded-r-full" />
                )}
                <button
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full
                    flex
                    items-center
                    gap-3.5
                    px-4
                    py-2.5
                    rounded-2xl
                    text-[13px]
                    font-bold
                    transition-all
                    duration-200
                    ${active
                      ? "bg-indigo-50/40 text-indigo-600"
                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                    }
                  `}
                >
                  <span className={`${active ? "text-indigo-600" : "text-gray-400"}`}>
                    {item.icon}
                  </span>
                  {item.text}
                </button>
              </div>
            );
          })}

          {/* Quick Capture Extension Download Widget (Hidden for now) */}
          {/*
          <div className="pt-3 mt-3 border-t border-gray-100">
            <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100/80 rounded-2xl shadow-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">⚡</span>
                <span className="text-xs font-bold text-indigo-900">Chrome Tool</span>
              </div>
              <p className="text-[11px] text-gray-500 mb-2 leading-tight font-medium">
                1-click candidate capture from Naukri with Ghost Mode stealth.
              </p>
              <a
                href="/workspace-extension.zip"
                download="workspace-extension.zip"
                className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download (ZIP)</span>
              </a>
            </div>
          </div>
          */}

        </div>

        {/* USER PROFILE & LOGOUT */}
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex flex-col gap-2">
          <div className="flex items-center gap-3 px-2 py-1">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-indigo-200">
              {user?.first_name?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-800 truncate leading-tight">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-[11px] text-gray-400 truncate leading-none mt-0.5">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-2xl text-xs font-bold transition duration-200"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* ================================================= */}
      {/* RIGHT CONTENT COLUMN */}
      {/* ================================================= */}
      <div className="flex-1 ml-0 md:ml-[260px] min-w-0 flex flex-col">
        {/* HEADER */}
        <header className="sticky top-0 z-40 h-[72px] bg-white border-b border-gray-100 px-6 flex items-center justify-between">
          {/* LEFT (Hamburger + Search) */}
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50 md:hidden"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Conditional Greeting / Mockup Search Bar */}
            {user?.designation_name === "Manager" ? (
              <div className="text-gray-800 animate-fadeIn">
                <h1 className="text-sm md:text-base font-black text-indigo-600 tracking-tight leading-none">
                  Welcome, {user?.first_name} {user?.last_name}!
                </h1>
                <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-wide">Morgan Manager Portal</p>
              </div>
            ) : (
              <div className="relative hidden md:block max-w-xs w-full">
                <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search for stats..."
                  className="w-full pl-9 pr-4 py-2 bg-[#f4f6fa] border-none rounded-2xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 placeholder-gray-400 text-gray-700 font-medium"
                />
              </div>
            )}
          </div>

          {/* RIGHT (Notification + Profile) */}
          <div className="flex items-center gap-3">
            {/* Quick Capture Chrome Extension Download (Hidden for now) */}
            {/*
            <a
              href="/workspace-extension.zip"
              download="workspace-extension.zip"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-2xl text-xs font-bold transition border border-indigo-200/60 shadow-sm"
              title="Download Chrome Quick Capture Extension (ZIP)"
            >
              <span>⚡</span>
              <span>Download Chrome Tool</span>
            </a>
            */}

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-10 h-10 rounded-2xl bg-[#f4f6fa]/60 text-gray-500 hover:bg-[#f4f6fa] hover:text-gray-700 transition duration-200"
              title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {theme === "dark" ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {user && (
              <div className="relative">
                <button
                  onClick={() => {
                    setOpenNotifications((prev) => !prev);
                    setOpenProfile(false);
                  }}
                  className="relative flex items-center justify-center w-10 h-10 rounded-2xl bg-[#f4f6fa]/60 text-gray-500 hover:bg-[#f4f6fa] hover:text-gray-700 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white" />
                  )}
                </button>

                {/* Notifications Dropdown */}
                {openNotifications && (
                  <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white border border-gray-100 rounded-2xl shadow-xl text-gray-800 animate-fadeIn z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <span className="text-xs font-bold text-gray-700">Notifications</span>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-[11px] text-indigo-600 hover:text-indigo-500 transition font-bold"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 no-scrollbar">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs text-gray-400">
                          No notifications yet
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            onClick={() => handleMarkRead(n.id, n.link)}
                            className={`px-4 py-3 text-xs text-left cursor-pointer hover:bg-gray-50 transition flex flex-col gap-1 ${
                              !n.is_read ? "bg-indigo-50/30" : ""
                            }`}
                          >
                            <p className="text-gray-800 font-semibold leading-snug">{n.message}</p>
                            <span className="text-[9px] text-gray-400">
                              {new Date(n.created_at).toLocaleString()}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </header>

        {/* CONTENT */}
        <main className="flex-1 p-6 overflow-y-auto">
          <Routes>

            <Route
              path="/"
              element={
                user?.designation_name === "Director" ? (
                  <DirectorDashboard />
                ) : user?.designation_name === "Manager" ? (
                  <ManagerDashboard />
                ) : (
                  <Home />
                )
              }
            />

            <Route
              path="talentpool"
              element={
                <TalentPool />
              }
            />
            <Route
              path="talentpool/results"
              element={
                <TalentPoolResults />
              }
            />

            <Route
              path="timetracking"
              element={
                <TimeTracking />
              }
            />

            <Route
              path="hierarchycrgchart"
              element={
                <HierarchyOrgChart />
              }
            />

            <Route
              path="callingsheet"
              element={
                <CallingSheet />
              }
            />

            {user &&
              (user.can_view_reports === 1 ||
                user.designation_name === "Director" ||
                user.designation_name === "Manager" ||
                user.designation_name === "Vice President" ||
                user.designation_name === "Account Manager") && (

              <Route
                path="businessreport"
                element={
                  <BusinessReport />
                }
              />
            )}

            <Route
              path="jobopening"
              element={
                <JobOpening />
              }
            />

            <Route
              path="vendor"
              element={
                <Vendor />
              }
            />

            <Route
              path="jobtable"
              element={
                <JobTable />
              }
            />

            <Route
              path="customizetable"
              element={
                <Customizetable />
              }
            />

            <Route
              path="myreport"
              element={
                <MyReports />
              }
            />

            <Route
  path="payslip"
  element={
    <MyPayslip />
  }
/>

            <Route
              path="workingeod"
              element={
                <UserSubmissionReport />
              }
            />

            <Route
              path="jobassignment"
              element={
                <JobAssignments />
              }
            />

          </Routes>
        </main>
      </div>

      {/* Optional Animation */}
      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
        `}
      </style>
    </div>
  );
}